import * as React from "react";

import {
    IconEmail,
    IconFacebook,
    IconFileAudio,
    IconFileGeneric,
    IconFilePDF,
    IconFileVideo,
    IconFileWeb,
    IconLinkedin,
    IconMapMarker,
    IconPhone,
    IconTwitter
} from "@common/components/Atoms/Icons";

interface AddressLinkProps {
    icon?: boolean;
    address: string;
    style?: any;
    className?: string;
}

export const AddressLink = (props: AddressLinkProps) => {
    const classes = ["c-structured-link"];
    const icon = <IconMapMarker />;
    if (props.className) classes.push(props.className);
    if (props.address) {
        return (
            <div className={classes.join(" ")} style={props.style}>
                {props.icon && (
                    <div className="c-structured-link__icon">{icon}</div>
                )}
                <div className="c-structured-link__info">
                    <a
                        href={`http://www.bing.com/maps/default.aspx?where1=${encodeURIComponent(
                            props.address
                        )}`}
                        className="c-structured-link__label c-link c-link--magenta"
                        target="_blank"
                    >
                        {props.address}
                    </a>
                </div>
            </div>
        );
    }
    return null;
};

// Attachment Link
interface AttachmentLinkProps {
    date?: any;
    icon?: boolean;
    label: string;
    style?: object;
    url: string;
    type?: string;
}

interface AttachmentLinkState{
    showAudioPlayer: boolean
    showVideoPlayer: boolean
}

export class AttachmentLink extends React.Component<AttachmentLinkProps, AttachmentLinkState> {
    
    constructor(props) {
        super(props);
        
        this.state = {
            showAudioPlayer: false,
            showVideoPlayer: false
        };

    }

    videoRef:any = null;
    audioRef:any = null;

    clickAudio = () => {
        this.setState(prevState=> {
            if(prevState.showAudioPlayer) this.audioRef.pause();
            else this.audioRef.play();
            return {...prevState, showAudioPlayer: !prevState.showAudioPlayer};            
        });
    }

    clickVideo = () => {
        this.setState(prevState=> {
            if(prevState.showVideoPlayer) this.videoRef.pause();
            else this.videoRef.play();
            return {...prevState, showVideoPlayer: !prevState.showVideoPlayer};            
        });
    }

    render() {
        const classes = ["c-structured-link"];
        let icon: JSX.Element | string = "";
        let type = "link";
        switch (this.props.type) {
            case "text/plain":
                icon = <IconFileGeneric />;
                break;
            case "application/pdf":
                icon = <IconFilePDF />;
                break;
            case "web":
                icon = <IconFileWeb />;
                break;
            case "audio/mpeg":
            case "audio/mp3":
            case "audio/ogg":
            case "application/ogg":
            case "audio/x-wav":
            case "audio/wav":
            case "audio/m4a":
            case "audio/x-m4a":
                icon = <IconFileAudio />;
                type = "audio";
                break;
            case "video/mp4":
            case "video/webm":
            case "video/ogg":
                icon = <IconFileVideo />;
                type = "video";
                break;
            default:
                icon = <IconFileGeneric />;
        }
        return (
            <div>
                <div className={classes.join(" ")} style={this.props.style}>
                    {this.props.icon && (
                        <div className="c-structured-link__icon">{icon}</div>
                    )}
                    <div className="c-structured-link__info">
                        <div style={{verticalAlign:"middle"}}>
                            {type==="link" && 
                                <a
                                    href={this.props.url}
                                    target="_blank"
                                    className="c-structured-link__label c-link c-link--magenta"
                                    title={`${this.props.label} ${type} ${this.props.date}`}
                                >
                                    {this.props.label}
                                </a>
                            }
                            {type==="audio" && 
                                <a
                                    onClick={this.clickAudio}
                                    target="_blank"
                                    className="c-structured-link__label c-link c-link--magenta"
                                    title={`${this.props.label} ${type} ${this.props.date}`}
                                >
                                    {this.props.label}
                                </a>
                            }
                            {type==="video" && 
                                <a
                                    onClick={this.clickVideo}
                                    target="_blank"
                                    className="c-structured-link__label c-link c-link--magenta"
                                    title={`${this.props.label} ${type} ${this.props.date}`}
                                >
                                    {this.props.label}
                                </a>
                            }
                            {this.props.date && (
                                <div className="c-structured-link__date">{this.props.date}</div>
                            )}
                        </div>
                    </div>
                </div>
                {type==="audio" && 
                    <div>
                        <audio controls={true} style={{display: this.state.showAudioPlayer ? "block" : "none"}}
                            ref={(ref)=>this.audioRef = ref}>
                            <source src={this.props.url} type={this.props.type} />
                            <a href={this.props.url} target="_blank">Click to open</a>
                        </audio>
                    </div>
                }
                {type==="video" && 
                    <div>
                        <video controls={true} style={{maxWidth:"480px", display: this.state.showVideoPlayer ? "block" : "none"}}
                            ref={(ref)=>this.videoRef = ref}>
                            <source src={this.props.url} type={this.props.type} />
                            <a href={this.props.url} target="_blank">Click to open</a>
                        </video>
                    </div>
                }
            </div>
        );
    }
};

// Email Link
interface EmailLinkProps {
    email?: string;
    icon?: boolean;
    className?: string;
    style?: object;
}

export const EmailLink = (props: EmailLinkProps) => {
    const classes = ["c-structured-link"];
    if (props.className) classes.push(props.className);
    if (props.email) {
        return (
            <div className={classes.join(" ")} style={props.style}>
                {props.icon && (
                    <div className="c-structured-link__icon">
                        <IconEmail />
                    </div>
                )}
                <div className="c-structured-link__info">
                    <a
                        href={`mailto:${props.email}`}
                        className={
                            "c-structured-link__label c-link c-link--magenta"
                        }
                    >
                        {props.email}
                    </a>
                </div>
            </div>
        );
    }
    return null;
};

// Facebook, LinkedIn, Twitter
interface UrlLinkProps {
    url: string;
}

export const FacebookLink = (props: UrlLinkProps) => {
    return (
        <a
            className={"c-button c-button--facebook"}
            href={props.url}
            target="_blank"
        >
            <IconFacebook /> post
        </a>
    );
};

export const LinkedInLink = (props: UrlLinkProps) => {
    return (
        <a
            className={"c-button c-button--linkedin"}
            href={props.url}
            target="_blank"
        >
            <IconLinkedin /> share
        </a>
    );
};

export const TwitterLink = (props: UrlLinkProps) => {
    return (
        <a
            className={"c-button c-button--twitter"}
            href={props.url}
            target="_blank"
        >
            <IconTwitter />
            tweet
        </a>
    );
};

// Tel Link
interface TelLinkProps {
    tel?: string;
    className?: string;
    display?: string;
    icon?: boolean;
    style?: object;
}

export const TelLink = (props: TelLinkProps) => {
    if (!props.tel) return null;

    const icon = <IconPhone />;
    const display =
        props.display ||
        (!!props.tel
            ? props.tel
                  .slice(-10)
                  .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
            : "");

    const classes = ["c-structured-link"];
    if (props.className) {
        classes.push(props.className);
    }

    return (
        <div className={classes.join(" ")} style={props.style}>
            {props.icon && (
                <div className="c-structured-link__icon">{icon}</div>
            )}
            <div className="c-structured-link__info">
                <a
                    href={`tel:${props.tel}`}
                    className="c-structured-link__label c-link c-link--magenta"
                >
                    {display}
                </a>
            </div>
        </div>
    );
};

export default EmailLink;
